import { Option, Text } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useStores } from '../../../common/hooks';
import { Organization } from '../../../models';
import { CommonLabelAndValueList } from '../../shared-components';
import { FlexBox } from '../../shared-components/flex-box';
import { FormattedInput, FormInput, SelectOptions } from '../../shared-components/form';
import { PhoneNumberField, PhoneNumberInput } from '../../shared-components/phone-number-input';
import { ValidationMessageList } from '../../shared-components/ValidationMessageList';

interface JointNomineesDetailFormProps {
  isViewOnly: boolean;
}

const JointNomineesDetailFormImpl: React.FC<JointNomineesDetailFormProps> = (props) => {
  const {
    isViewOnly,
  } = props;

  const {
    nominationForStaffPageUiStore,
    countryStore,
  } = useStores();

  const renderJointNomineeDetailForm = (jointNominee: Organization, index: number): React.JSX.Element => {
    const validation = nominationForStaffPageUiStore.jointNomineeValidations[index];
    const idSuffix = `joint-nominee-${index}`;

    return (
      <FlexBox
        key={index}
        isFullWidth
        direction='column'
        className='fields__list'
      >
        <FlexBox isFullWidth aligns='start'>
          <CommonLabelAndValueList
            labelId={`partner-id-${idSuffix}`}
            label={`Nominee #${index + 1} - Partner ID:`}
            childrenFallback={jointNominee.mpnId}
          >
            {
              !isViewOnly &&
              <FormInput
                validationMessage={
                  validation.fieldErrors.mpnId?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.mpnId} />
                    : undefined
                }
                onChange={mpnId => jointNominee.mpnId = mpnId}
                inputProps={{
                  'aria-labelledby': `partner-id-${idSuffix}`,
                  type: 'number',
                  value: jointNominee.mpnId,
                  maxLength: 100,
                }}
              />
            }
          </CommonLabelAndValueList>
          <CommonLabelAndValueList
            labelId={`company-${idSuffix}`}
            label='Company:'
            childrenFallback={jointNominee.companyName}
          >
            {
              !isViewOnly &&
              <FormInput
                validationMessage={
                  validation.fieldErrors.companyName?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.companyName} />
                    : undefined
                }
                onChange={companyName => jointNominee.companyName = companyName}
                inputProps={{
                  'aria-labelledby': `company-${idSuffix}`,
                  value: jointNominee.companyName,
                  maxLength: 100,
                }}
              />
            }
          </CommonLabelAndValueList>
        </FlexBox>
        <FlexBox isFullWidth aligns='start'>
          <CommonLabelAndValueList
            labelId={`address-1-${idSuffix}`}
            label='Address 1:'
            childrenFallback={jointNominee.address1}
          >
            {
              !isViewOnly &&
              <FormInput
                validationMessage={
                  validation.fieldErrors.address1?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.address1} />
                    : undefined
                }
                onChange={address1 => jointNominee.address1 = address1}
                inputProps={{
                  'aria-labelledby': `address-1-${idSuffix}`,
                  value: jointNominee.address1,
                  maxLength: 100,
                }}
              />
            }
          </CommonLabelAndValueList>
          <CommonLabelAndValueList
            labelId={`address-2-${idSuffix}`}
            label='Address 2:'
            childrenFallback={jointNominee.address2}
          >
            {
              !isViewOnly &&
              <FormInput
                validationMessage={
                  validation.fieldErrors.address2?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.address2} />
                    : undefined
                }
                onChange={address2 => jointNominee.address2 = address2}
                inputProps={{
                  'aria-labelledby': `address-2-${idSuffix}`,
                  value: jointNominee.address2,
                  maxLength: 100,
                }}
              />
            }
          </CommonLabelAndValueList>
        </FlexBox>
        <FlexBox isFullWidth aligns='start'>
          <CommonLabelAndValueList
            labelId={`city-${idSuffix}`}
            label='City:'
            childrenFallback={jointNominee.city}
          >
            {
              !isViewOnly &&
              <FormInput
                validationMessage={
                  validation.fieldErrors.city?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.city} />
                    : undefined
                }
                onChange={city => jointNominee.city = city}
                inputProps={{
                  'aria-labelledby': `city-${idSuffix}`,
                  value: jointNominee.city,
                  maxLength: 100,
                }}
              />
            }
          </CommonLabelAndValueList>
          <CommonLabelAndValueList
            labelId={`state-${idSuffix}`}
            label='State:'
            childrenFallback={jointNominee.stateProvince}
          >
            {
              !isViewOnly &&
              <FormInput
                validationMessage={
                  validation.fieldErrors.stateProvince?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.stateProvince} />
                    : undefined
                }
                onChange={stateProvince => jointNominee.stateProvince = stateProvince}
                inputProps={{
                  'aria-labelledby': `state-${idSuffix}`,
                  value: jointNominee.stateProvince,
                  maxLength: 100,
                }}
              />
            }
          </CommonLabelAndValueList>
        </FlexBox>
        <FlexBox isFullWidth aligns='start'>
          <CommonLabelAndValueList
            labelId={`postal-code-${idSuffix}`}
            label='Postal code:'
            childrenFallback={jointNominee.postalCode}
          >
            {
              !isViewOnly &&
              <FormInput
                validationMessage={
                  validation.fieldErrors.postalCode?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.postalCode} />
                    : undefined
                }
                onChange={postalCode => jointNominee.postalCode = postalCode}
                inputProps={{
                  'aria-labelledby': `postal-code-${idSuffix}`,
                  value: jointNominee.postalCode,
                  maxLength: 100,
                }}
              />
            }
          </CommonLabelAndValueList>
          <CommonLabelAndValueList
            labelId={`country-${idSuffix}`}
            label='Country:'
            childrenFallback={jointNominee.country.displayName}
          >
            {
              !isViewOnly &&
              <SelectOptions
                onOptionSelect={(data) => {
                  if (!data.optionValue) return;
                  const country = countryStore.awardCountries.find(country => country.countryCode === data.optionValue);
                  if (!country) return;
                  jointNominee.country = { ...country };
                }}
                validationMessage={
                  validation.fieldErrors.country?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.country} />
                    : undefined
                }
                dropdownProps={{
                  'aria-labelledby': `country-${idSuffix}`,
                  value: jointNominee.country.displayName,
                  selectedOptions: [jointNominee.country.countryCode],
                }}
              >
                {
                  countryStore.awardCountries.map(country => (
                    <Option key={country.countryCode} value={country.countryCode}>{country.displayName}</Option>
                  ))
                }
              </SelectOptions>
            }
          </CommonLabelAndValueList>
        </FlexBox>
        <FlexBox isFullWidth aligns='start'>
          <CommonLabelAndValueList
            label='Company phone number:'
            labelId='nominee-phone-number'
            childrenFallback={jointNominee.phoneNumber}
          >
            {
              !isViewOnly &&
              <PhoneNumberInput
                id={PhoneNumberField.JointNominee}
                ariaLabelledBy='nominee-phone-number'
                value={jointNominee.phoneNumber}
                onChange={(phoneNumber) => {
                  jointNominee.phoneNumber = phoneNumber
                }}
                onCountryChange={(id, newCountryCode) => {
                  nominationForStaffPageUiStore.setSelectedCountryCode(id, newCountryCode);
                }}
                error={
                  validation.fieldErrors.phoneNumber?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.phoneNumber} />
                    : undefined
                }
              />
            }
          </CommonLabelAndValueList>
          <CommonLabelAndValueList
            label='Website URL:'
            labelId='nominee-website-url'
            childrenFallback={jointNominee.websiteUrl}
          >
            {
              !isViewOnly &&
              <FormattedInput
                prefix={'https://'}
                orientation='horizontal'
                maxLength={100}
                isValidated
                value={jointNominee.websiteUrl}
                onChange={websiteUrl => jointNominee.websiteUrl = websiteUrl}
                ariaLabelledBy='nominee-website-url'
                errorText={
                  validation.fieldErrors.websiteUrl?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.websiteUrl} />
                    : undefined
                }
              />
            }
          </CommonLabelAndValueList>
        </FlexBox>
        <FlexBox isFullWidth aligns='start'>
          <CommonLabelAndValueList
            labelId={`prefix-${idSuffix}`}
            label='Prefix:'
            childrenFallback={jointNominee.prefix}
          >
            {
              !isViewOnly &&
              <SelectOptions
                onOptionSelect={(data) => jointNominee.prefix = data.optionValue ? data.optionValue : ''}
                validationMessage={
                  validation.fieldErrors.prefix?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.prefix} />
                    : undefined
                }
                dropdownProps={{
                  'aria-labelledby': `prefix-${idSuffix}`,
                  value: jointNominee.prefix ? jointNominee.prefix.trim() : '',
                  selectedOptions: [jointNominee.prefix.trim()],
                }}
              >
                <Option text='' />
                <Option value='Mr.'>Mr.</Option>
                <Option value='Mrs.'>Mrs.</Option>
                <Option value='Ms.'>Ms.</Option>
              </SelectOptions>
            }
          </CommonLabelAndValueList>
          <CommonLabelAndValueList
            labelId={`first-name-${idSuffix}`}
            label='First name:'
            childrenFallback={jointNominee.firstName}
          >
            {
              !isViewOnly &&
              <FormInput
                validationMessage={
                  validation.fieldErrors.firstName?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.firstName} />
                    : undefined
                }
                onChange={firstName => jointNominee.firstName = firstName}
                inputProps={{
                  'aria-labelledby': `first-name-${idSuffix}`,
                  value: jointNominee.firstName,
                  maxLength: 100,
                }}
              />
            }
          </CommonLabelAndValueList>
        </FlexBox>
        <FlexBox isFullWidth aligns='start'>
          <CommonLabelAndValueList
            labelId={`last-name-${idSuffix}`}
            label='Last name:'
            childrenFallback={jointNominee.lastName}
          >
            {
              !isViewOnly &&
              <FormInput
                validationMessage={
                  validation.fieldErrors.lastName?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.lastName} />
                    : undefined
                }
                onChange={lastName => jointNominee.lastName = lastName}
                inputProps={{
                  'aria-labelledby': `last-name-${idSuffix}`,
                  value: jointNominee.lastName,
                  maxLength: 100,
                }}
              />
            }
          </CommonLabelAndValueList>
          <CommonLabelAndValueList
            labelId={`contact-email-${idSuffix}`}
            label='Contact email:'
            childrenFallback={jointNominee.emailAddress}
          >
            {
              !isViewOnly &&
              <FormInput
                validationMessage={
                  validation.fieldErrors.emailAddress?.length
                    ? <ValidationMessageList messages={validation.fieldErrors.emailAddress} />
                    : undefined
                }
                onChange={emailAddress => jointNominee.emailAddress = emailAddress}
                inputProps={{
                  'aria-labelledby': `contact-email-${idSuffix}`,
                  value: jointNominee.emailAddress,
                  maxLength: 100,
                }}
              />
            }
          </CommonLabelAndValueList>
        </FlexBox>
      </FlexBox>
    );
  }

  return (
    <FlexBox
      isFullWidth
      as='section'
      className='nomination-details__fields'
    >
      <Text
        as='h3'
        block
        weight='semibold'
      >
        Joint Nominee
      </Text>
      <FlexBox
        isFullWidth
        direction='column'
        className='fields__list'
      >
        {
          nominationForStaffPageUiStore.nominationForStaff.nomination.jointNominees.map(renderJointNomineeDetailForm)
        }
      </FlexBox>
    </FlexBox>
  )
}

export const JointNomineesDetailForm = observer(JointNomineesDetailFormImpl);
